/**
 * ExamineObjectDialog - Dialogue pour examiner un objet en plein écran avec zoom
 * Identique à Flutter ExamineObjectDialog
 */

import React, {useState, useRef, useEffect} from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  SafeAreaView,
  Platform,
  Image,
  TouchableOpacity,
  PanResponder,
  Animated,
  Dimensions,
} from 'react-native';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faTimes} from '@fortawesome/free-solid-svg-icons';
import {SolidObject} from '../models/SolidObject';
import {Fonts} from '../config/fonts';

interface ExamineObjectDialogProps {
  visible: boolean;
  object: SolidObject | null;
  onClose: () => void;
}

const ExamineObjectDialog: React.FC<ExamineObjectDialogProps> = ({visible, object, onClose}) => {
  const [scale, setScale] = useState(1);
  const pan = useRef(new Animated.ValueXY()).current;
  
  // Réinitialiser le zoom et la position quand le dialog s'ouvre/ferme
  useEffect(() => {
    if (!visible) {
      setScale(1);
      pan.setValue({x: 0, y: 0});
    }
  }, [visible]);

  if (!object) {
    return null;
  }

  const getImageUri = (imageData?: string): string | null => {
    if (!imageData) return null;
    if (imageData.startsWith('data:image')) {
      return imageData;
    }
    return `data:image/png;base64,${imageData}`;
  };

  const imageUri = getImageUri(object.image);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        pan.setOffset({
          x: (pan.x as any)._value,
          y: (pan.y as any)._value,
        });
      },
      onPanResponderMove: (evt, gestureState) => {
        pan.setValue({x: gestureState.dx, y: gestureState.dy});
      },
      onPanResponderRelease: () => {
        pan.flattenOffset();
      },
    }),
  ).current;

  // Pour le zoom, on utilise un geste de pincement simplifié (double-tap pour zoom)
  const handleDoubleTap = () => {
    if (scale === 1) {
      setScale(2);
    } else {
      setScale(1);
      pan.setValue({x: 0, y: 0});
    }
  };
  

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.container}>
        <SafeAreaView style={styles.safeArea}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>{object.name}</Text>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <FontAwesomeIcon icon={faTimes} size={24} color="#fff" />
            </TouchableOpacity>
          </View>

          {/* Image avec zoom/pan */}
          <View style={styles.imageContainer} {...panResponder.panHandlers}>
            {imageUri ? (
              <Animated.View
                style={[
                  styles.imageWrapper,
                  {
                    transform: [
                      {scale: scale},
                      {
                        translateX: pan.x,
                      },
                      {
                        translateY: pan.y,
                      },
                    ],
                  },
                ]}>
                <TouchableOpacity
                  activeOpacity={1}
                  onPress={handleDoubleTap}
                  style={styles.imageTouchable}>
                  <Image source={{uri: imageUri}} style={styles.image} resizeMode="contain" />
                </TouchableOpacity>
              </Animated.View>
            ) : (
              <View style={styles.placeholder}>
                <FontAwesomeIcon icon={faTimes} size={64} color="#666" />
              </View>
            )}
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>
              N'hésitez pas à zoomer et examiner minutieusement l'objet...
            </Text>
          </View>
        </SafeAreaView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
  title: {
    flex: 1,
    fontSize: 20,
    fontWeight: '600',
    color: '#fff',
    fontFamily: Fonts.title,
  },
  closeButton: {
    padding: 8,
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#000',
  },
  imageWrapper: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageTouchable: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  placeholder: {
    width: 200,
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#333',
  },
  footer: {
    padding: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  footerText: {
    fontSize: 14,
    color: '#fff',
    textAlign: 'center',
    fontFamily: Fonts.reading,
    fontStyle: 'italic',
  },
});

export default ExamineObjectDialog;
